import { isNonEmptyArray } from '@apollo/client/utilities';
import { BeneficiaryAddress } from 'src/app/_core/services/mail.service';
import { SharedMail } from 'src/app/_shared/components/mail/shared-mail/shared-mail.component';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { getXmlBoolean, getXmlLine } from 'src/app/utils/soap.utils';
import {
    DestinataireMailModelDtoInput, FichierModel,
    MailModelDtoInput,
    ModelesModelEmailPieceJointeModelInput
} from 'src/graphql/generated-types';

export const generateSendMailWithCourrierXML =
    (reportCode: string, mail: MailModelDtoInput, parameters: { [key: string]: any }): string => {
        const xmlParameters = generateXmlDictionnary(parameters);
        const xmlMail = generateXMLMail(mail);
        return `
            <CodeReport>${reportCode}</CodeReport>
             ${xmlParameters}
             ${xmlMail}
        `;
    };

const generateXMLMail = (mail: MailModelDtoInput): string => {
    return `
            <mail xmlns:d4p1="http://schemas.datacontract.org/2004/07/V2DTO">
                ${[
                    'AdrPrivilegierPerso',
                    'AdrPrivilegierPro',
                    'AdresseLesDeux',
                    'AdressePerso',
                    'AdressePrivilegier',
                    'AdressePro',
                    'AdrPrivilegierPro'
                ].map(key => getXmlLine(mail, key, {defaultValue: false})).join('')}
                <d4p1:Body>
                    <![CDATA[${mail.Body || ''}]]>
                </d4p1:Body>
                ${getXmlLine(mail, 'CopieCE', {defaultValue: false})}
                ${getXmlLine(mail, 'From', {defaultValue: ''})}
                ${getXmlLine(mail, 'IdBenef', {defaultValue: 0})}
                ${getXmlLine(mail, 'IsHTML', {defaultValue: false})}
                <d4p1:ListDestinataire>
                    ${generateXmlRecipientList(mail.ListDestinataire.DestinataireMailModelDTO)}
                </d4p1:ListDestinataire>
                <d4p1:ListFichier>
                </d4p1:ListFichier>
                ${getXmlLine(mail, 'ReplyTo', {defaultValue: ''})}
                ${getXmlLine(mail, 'SendMail', {defaultValue: false})}
                ${getXmlLine(mail, 'Subject', {defaultValue: false})}
            </mail>`;
};

const generateXmlDictionnary = (parameters: {[key: string]: any}): string => {
    const xmlParameters = Object.keys(parameters).reduce((acc, k) => {
        return acc + `<d4p1:KeyValueOfstringanyType>
                        <d4p1:Key>${k}</d4p1:Key>
                        <d4p1:Value i:type="d6p1:${getXmlType(parameters[k])}">${parameters[k]}</d4p1:Value>
                      </d4p1:KeyValueOfstringanyType>`;
    }, '');

    return `<dictionaryParameters xmlns:d4p1="http://schemas.microsoft.com/2003/10/Serialization/Arrays"
                                  xmlns:i="http://www.w3.org/2001/XMLSchema-instance"
                                  xmlns:d6p1="http://www.w3.org/2001/XMLSchema">
                ${xmlParameters}
            </dictionaryParameters>`;
};

// Génération de la liste de fichiers à mettre en pièce jointe d'un email
const generateXmlFileList = (fileList: FichierModel[]): string => {
    if (!(fileList && isNonEmptyArray(fileList))) {
        return '';
    }

    return fileList.map(f => `
        <d4p1:FichierModel>
            ${!f.FileBytes ? '' : getXmlLine(f, 'FileBytes', {defaultValue: '', namespace: 'd4p1'})}
            ${getXmlLine(f, 'Filename', {defaultValue: '', namespace: 'd4p1'})}
            ${getXmlLine(f, 'IdFile', {defaultValue: null, namespace: 'd4p1'})}
            ${getXmlLine(f, 'LibelleLien', {defaultValue: '', namespace: 'd4p1'})}
        </d4p1:FichierModel>
    `).join('');
};

const generateXmlRecipientList = (recipientList: DestinataireMailModelDtoInput[]): string => {
    if (!(recipientList && arrayNotEmpty(recipientList))) {
        return '';
    }
    return recipientList.map(r => `
            <d4pi:DestinataireMailModelDtoInput>
                <d4p1:Adresse>${r.Adresse}</d4p1:Adresse>
                <d4p1:AdressePro>${r.AdressePro}</d4p1:AdressePro>
                <d4p1:IdBenef>${r.IdBenef}</d4p1:IdBenef>
            </d4pi:DestinataireMailModelDtoInput>
        `).join();
};

// noinspection JSMethodCanBeStatic
const getXmlType = (value: any): string => {
    const type = typeof value;

    switch (type) {
        case 'number' : {
            return 'decimal';
        }
        case 'boolean': {
            return 'boolean';
        }
        case 'string' : {
            return 'string';
        }
        default : {
            throw new Error('type \'' + value + '\' inconnu');
        }
    }
};

export const generateEnvoieMailPublipostageXML =
    (publipostageMail: ModelesModelEmailPieceJointeModelInput, beneficiaryAddress: BeneficiaryAddress, mail: SharedMail, idsList: number[]): string => {

        const {
            IsEmailPerso,
            IsEmailPro,
            IsEmailPriviligie,
        } = beneficiaryAddress;

        return `
        ${getModeleEmailXML(publipostageMail)}
        <criteresRechercheBenef xmlns:d4p1="http://schemas.datacontract.org/2004/07/V2DTO">
            ${getXmlLine(publipostageMail, 'CorpsModelEmail')}
            <d4p1:IsEmailPerso>${!IsEmailPriviligie && IsEmailPerso}</d4p1:IsEmailPerso>
            ${getXmlBoolean(beneficiaryAddress, 'IsEmailPriviligie')}
            ${getXmlBoolean(beneficiaryAddress, 'IsEmailPriviligiePerso')}
            ${getXmlBoolean(beneficiaryAddress, 'IsEmailPriviligiePro')}
            <d4p1:IsEmailPro>${!IsEmailPriviligie && IsEmailPro}</d4p1:IsEmailPro>
            ${getXmlBoolean(beneficiaryAddress, 'IsEmailProfOrPerso')}
        </criteresRechercheBenef>
        <idTypePubli>${mail.publipostageTypeId}</idTypePubli>
        <listeId xmlns:array="http://schemas.microsoft.com/2003/10/Serialization/Arrays">
            ${arrayNotEmpty(idsList) ? idsList.map(id => `<array:decimal>${id}</array:decimal>`).join('') : ''}
        </listeId>
        <isGenerationCourrierEnabled>false</isGenerationCourrierEnabled>
        <isNewsLetter>${mail.isNewsLetter || false}</isNewsLetter>`;
    };

export const generateModeleEMailPublipostageXML =
    (mail: ModelesModelEmailPieceJointeModelInput, beneficiaryId: number, idTypePubli: number): string => {
        return `
        ${getModeleEmailXML(mail)}
        <idTypePubli>${idTypePubli}</idTypePubli>
        <listeId>${beneficiaryId}</listeId>`;
    };

const getModeleEmailXML = (publipostageMail: ModelesModelEmailPieceJointeModelInput): string => {
    return `
    <modeleEmail xmlns:modele="http://schemas.datacontract.org/2004/07/V2DTO.Publipostage.Modeles"
        xmlns:d4p1="http://schemas.datacontract.org/2004/07/V2DTO"
        xmlns:array="http://schemas.microsoft.com/2003/10/Serialization/Arrays">
        ${getXmlLine(publipostageMail, 'CorpsModelEmail', {namespace: 'modele'})}
        ${getXmlLine(publipostageMail, 'EmetteurModelEmail', {namespace: 'modele'})}
        ${getXmlLine(publipostageMail, 'IdModelEmail', {defaultValue: 0, namespace: 'modele'})}
        ${getXmlLine(publipostageMail, 'IsIdentifiants', {defaultValue: false, namespace: 'modele'})}
        ${getXmlLine(publipostageMail, 'IsNewsLetter', {defaultValue: false, namespace: 'modele'})}
        ${getXmlLine(publipostageMail, 'IsPubliPostage', {defaultValue: false, namespace: 'modele'})}
        <modele:ListeIdReportJoint >${arrayNotEmpty(publipostageMail.ListeIdReportJoint?.decimal) ? publipostageMail.ListeIdReportJoint.decimal.map(id => `<array:decimal>${id}</array:decimal>`).join('') : ''}</modele:ListeIdReportJoint>
        <modele:ListeIdTheme>${arrayNotEmpty(publipostageMail.ListeIdTheme?.decimal) ? publipostageMail.ListeIdTheme.decimal.map(id => `<array:decimal>${id}</array:decimal>`).join('') : ''}</modele:ListeIdTheme>
        <modele:ListePJ>
        ${generateXmlFileList(publipostageMail.ListePJ.FichierModel)}
        </modele:ListePJ>
        ${getXmlLine(publipostageMail, 'ObjectModelEmail', {defaultValue: false, namespace: 'modele'})}
    </modeleEmail>`;
};
